import { z } from 'zod';

/**
 * Schema live-config. Khớp từng dòng docs/config-table.md (test/table.test.ts kiểm hai chiều).
 * Mỗi lá (leaf) của object = một dòng bảng; record/mảng là lá.
 * Ràng buộc chéo (mục 13 của bảng) nằm ở checkConfigInvariants().
 */

export const COLORS = ['R', 'B', 'G', 'Y', 'P'] as const;
export type Color = (typeof COLORS)[number];

/** Kind sinh ra từ spawn, đúng thứ tự rút trọng số (core phải dùng thứ tự này, không dùng thứ tự khoá JSON). */
export const SPAWN_KINDS = ['basic', 'kin', 'far', 'laneH', 'laneV', 'diag', 'essence'] as const;
export type SpawnKind = (typeof SPAWN_KINDS)[number];

/** Nhóm luật nối (link.rules.*). */
export const LINK_RULE_KINDS = [...SPAWN_KINDS, 'booster', 'lightball'] as const;
export type LinkRuleKind = (typeof LINK_RULE_KINDS)[number];

export const DEBUFF_ELIGIBLE_KINDS = ['basic', 'kin', 'far', 'laneH', 'laneV', 'diag'] as const;
export const PROPELLER_TARGETS = [
  'deadMark',
  'frozen',
  'locked',
  'essence',
  'booster',
  'any',
] as const;
export const LINK_AXES = ['any', 'ortho', 'h', 'v', 'diag'] as const;
export const LINK_FILTERS = ['any', 'sameColor', 'sameKind'] as const;

/** semver 2.0.0 (không cho tiền tố v). */
export const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

// ---- helper kiểu theo cột type của bảng ----
const int = (min: number, max: number) => z.int().min(min).max(max);
/** pct / bp đều là số nguyên trong lockstep. */
const pct = int;
const num = (min: number, max: number) => z.number().min(min).max(max);
const bool = () => z.boolean();
const color = z.enum(COLORS);
const nonEmpty = z.string().min(1);

const linkRule = z.strictObject({
  range: int(1, 9),
  axis: z.enum(LINK_AXES),
  filter: z.enum(LINK_FILTERS),
  canStart: bool(),
  canChain: bool(),
});

const linkRules = z.strictObject(
  Object.fromEntries(LINK_RULE_KINDS.map((k) => [k, linkRule])) as Record<
    LinkRuleKind,
    typeof linkRule
  >,
);

const board = z.strictObject({
  width: int(5, 9),
  height: int(5, 9),
  colors: z.array(color).min(3).max(5),
  spawn: z.strictObject({
    colorWeights: z.partialRecord(color, int(0, 1000)),
    kindWeights: z.record(z.enum(SPAWN_KINDS), int(0, 1000)),
    essenceMaxOnBoard: int(0, 10),
  }),
  initialMinValidMoves: int(1, 10),
  autoShuffleWhenNoMoves: bool(),
  shuffleMaxAttempts: int(1, 50),
});

const link = z.strictObject({
  minLength: int(2, 5),
  prefixFilter: bool(),
  allowBacktrack: bool(),
  boosterComboAnyAdjacency: bool(),
  tapModeEnabled: bool(),
  dragHitRadiusPct: pct(40, 100),
  rules: linkRules,
});

const booster = z.strictObject({
  spawnFromNormal: bool(),
  spawnFromAbility: bool(),
  propeller: z.strictObject({
    length: int(3, 8),
    requiresSquare: bool(),
    plusRadius: int(0, 2),
    targetPriority: z.array(z.enum(PROPELLER_TARGETS)).min(1).max(6),
  }),
  rocket: z.strictObject({ minLength: int(3, 12) }),
  bomb: z.strictObject({ minLength: int(4, 15), radius: int(1, 3) }),
  lightball: z.strictObject({
    minLength: int(5, 20),
    tapColorRule: z.enum(['mostCommon', 'random']),
  }),
  comboEnabled: bool(),
  combo: z.strictObject({
    rocketBomb: z.strictObject({ width: int(1, 5) }),
    bombBomb: z.strictObject({ radius: int(1, 4) }),
    propellerPropeller: z.strictObject({ count: int(1, 6) }),
  }),
  newBoosterShielded: bool(),
});

const essence = z.strictObject({
  startCharge: int(1, 5),
  mergeMinCount: int(2, 5),
  mergeBonusPct: pct(0, 200),
  maxCharge: int(2, 99),
  tierMinCharge: z.array(z.int().min(1)).min(2).max(6),
  radiusByTier: z.array(z.int().min(0)).min(2).max(6),
  powerPct: pct(0, 1000),
  superlinearPct: pct(0, 200),
  mergeOnlyConsumesTurn: bool(),
});

const combat = z.strictObject({
  lengthBonusPct: pct(0, 100),
  lengthBonusDecayPct: pct(0, 100),
  collateralPct: pct(0, 200),
  minDamage: int(0, 10),
  critDmgPct: pct(100, 500),
  defenseScale: int(1, 10000),
  element: z.strictObject({
    advantagePct: pct(100, 300),
    disadvantagePct: pct(10, 100),
    beats: z.partialRecord(color, z.array(color)),
  }),
  defaultTarget: z.enum(['firstAlive', 'lowestHp']),
  overflowToNextTarget: bool(),
  preferAbilityOverNormal: bool(),
  invalidReleaseConsumesTurn: bool(),
  ult: z.strictObject({
    gaugeMax: int(5, 200),
    gainPerPathOrb: int(0, 10),
    gainPerCollateral: int(0, 10),
  }),
});

const debuff = z.strictObject({
  maxPerOrb: int(1, 1),
  eligibleKinds: z.array(z.enum(DEBUFF_ELIGIBLE_KINDS)).min(1).max(6),
  lock: z.strictObject({ defaultDurationTurns: int(0, 20), adjacentChainUnlock: bool() }),
  freeze: z.strictObject({ maxLayers: int(1, 5), adjacentChainHits: bool() }),
  deadMark: z.strictObject({ defaultTurns: int(1, 10), defuseOnConsume: bool() }),
});

const battle = z.strictObject({
  newDebuffTicksSameRound: bool(),
  waveTransitionSkipsMonsterPhase: bool(),
  maxRoundsPerBattle: int(20, 1000),
});

const dungeon = z.strictObject({
  floorsPerRun: int(1, 50),
  buffChoiceCount: int(1, 5),
  healBetweenFloorsPct: pct(0, 100),
  rerollBuffCount: int(0, 5),
});

const ui = z.strictObject({
  showLinkRange: bool(),
  dimNonCandidatesPct: pct(0, 90),
  showDamagePreview: bool(),
  showIntent: bool(),
  hintDelaySec: int(0, 60),
  anim: z.strictObject({
    speedPct: pct(25, 400),
    popMs: int(0, 1000),
    chainStepMs: int(0, 500),
    fallMsPerCell: int(0, 500),
    boosterMs: int(0, 2000),
    monsterActionMs: int(0, 3000),
  }),
  layout: z.strictObject({
    sideGutterPx: int(0, 48),
    hudHeightPx: int(32, 80),
    skillBarHeightPx: int(48, 120),
    sceneMinHeightPx: int(120, 480),
    boardMaxWidthPx: int(280, 720),
    pixelIntegerScale: bool(),
  }),
  audio: z.strictObject({ masterVolumePct: pct(0, 100) }),
});

const economy = z.strictObject({
  currencies: z.array(nonEmpty).min(1).max(10),
  exchange: z.strictObject({
    diamondToGold: int(1, 100000),
    diamondToMaterial: int(1, 100000),
    diamondToToken: int(1, 100000),
  }),
  dungeonGoldPerFloor: int(0, 100000),
});

const pvpRating = z.strictObject({
  placementMatches: int(0, 20),
  seasonLengthDays: int(7, 365),
  forfeitGraceSec: int(5, 300),
  mmr: z.strictObject({
    initial: int(0, 5000),
    kFactor: z.strictObject({
      placement: int(1, 200),
      base: int(1, 200),
      highRating: int(1, 200),
    }),
    highRatingThreshold: int(0, 5000),
    inactivityDecayPerWeek: int(0, 100),
  }),
  display: z.strictObject({
    initial: int(0, 5000),
    baseGain: int(0, 200),
    baseLoss: int(0, 200),
    mmrMapBase: int(0, 5000),
    convergeFactor: num(0, 2),
    convergeScale: int(1, 5000),
    minDelta: int(0, 200),
    maxDelta: int(0, 500),
    winStreakBonus: int(0, 100),
    winStreakMin: int(2, 20),
    tiers: z
      .array(z.strictObject({ id: nonEmpty, minDisplay: z.int().min(0) }))
      .min(1)
      .max(12),
    tierFloorProtection: int(0, 20),
    seasonResetMode: z.enum(['softFactor', 'tierDrop']),
    seasonSoftResetFactor: num(0, 1),
    seasonResetTierDrop: int(0, 5),
  }),
});

const pvpMatchmaking = z.strictObject({
  regions: z.array(nonEmpty).min(1).max(20),
  tickMs: int(100, 10000),
  windowStart: int(0, 2000),
  windowStep: int(0, 1000),
  windowStepSec: int(1, 120),
  windowMax: int(0, 5000),
  maxWaitSec: int(5, 600),
  samePairLimitPerDay: int(1, 50),
  dodgePenaltySec: z.array(z.int().min(0)).min(1).max(10),
  queueRateLimitPerMin: int(1, 120),
  minAccountLevel: int(1, 100),
  turnTimeoutSec: int(5, 120),
  afkTurnsToForfeit: int(1, 10),
  botFallbackEnabled: bool(),
  botFallbackAfterSec: int(5, 600),
  botFallbackRevealBot: bool(),
  botFallbackDifficultyByMmr: z
    .array(z.strictObject({ minMmr: z.int().min(0), difficulty: nonEmpty }))
    .min(1)
    .max(10),
  botFallbackRewardMultiplier: num(0, 1),
  botFallbackMaxPerDay: int(0, 100),
});

/** Object config không kèm ràng buộc chéo (dùng sinh JSON Schema và test min/max từng key). */
export const configObjectSchema = z.strictObject({
  configVersion: z.string().regex(SEMVER_RE, 'configVersion phải là semver'),
  board,
  link,
  booster,
  essence,
  combat,
  debuff,
  battle,
  dungeon,
  ui,
  economy,
  pvp: z.strictObject({ rating: pvpRating, matchmaking: pvpMatchmaking }),
});

export type Config = z.infer<typeof configObjectSchema>;

export interface ConfigIssue {
  path: (string | number)[];
  message: string;
}

function hasDuplicates(values: readonly unknown[]): boolean {
  return new Set(values).size !== values.length;
}

function strictlyIncreasing(values: readonly number[]): boolean {
  return values.every((v, i) => i === 0 || v > values[i - 1]!);
}

/** Ràng buộc chéo — mục 13 config-table + contract r1-3/r1-4. Trả danh sách lỗi (rỗng = hợp lệ). */
export function checkConfigInvariants(c: Config): ConfigIssue[] {
  const issues: ConfigIssue[] = [];
  const add = (path: string, message: string) => issues.push({ path: path.split('.'), message });

  // board
  if (hasDuplicates(c.board.colors)) add('board.colors', 'board.colors không được trùng');
  const weightKeys = Object.keys(c.board.spawn.colorWeights).sort();
  const colorKeys = [...c.board.colors].sort();
  if (weightKeys.join(',') !== colorKeys.join(','))
    add('board.spawn.colorWeights', 'khoá colorWeights phải đúng bằng board.colors');
  const weightSum = Object.values(c.board.spawn.colorWeights).reduce<number>(
    (s, v) => s + (v ?? 0),
    0,
  );
  if (weightSum <= 0) add('board.spawn.colorWeights', 'tổng colorWeights phải > 0');
  if (c.board.spawn.kindWeights.basic <= 0)
    add('board.spawn.kindWeights.basic', 'kindWeights.basic phải > 0');

  // link
  if (c.link.minLength > Math.min(c.board.width, c.board.height))
    add('link.minLength', 'link.minLength phải ≤ min(board.width, board.height)');

  // booster
  const b = c.booster;
  if (!(
    b.propeller.length < b.rocket.minLength &&
    b.rocket.minLength < b.bomb.minLength &&
    b.bomb.minLength < b.lightball.minLength
  ))
    add(
      'booster.propeller.length',
      'cần propeller.length < rocket.minLength < bomb.minLength < lightball.minLength',
    );
  const tp = b.propeller.targetPriority;
  if (tp[tp.length - 1] !== 'any')
    add('booster.propeller.targetPriority', 'phần tử cuối phải là "any"');
  if (hasDuplicates(tp)) add('booster.propeller.targetPriority', 'không được trùng');
  if (b.combo.rocketBomb.width % 2 !== 1) add('booster.combo.rocketBomb.width', 'phải là số lẻ');

  // essence
  const e = c.essence;
  if (!strictlyIncreasing(e.tierMinCharge)) add('essence.tierMinCharge', 'phải tăng nghiêm ngặt');
  if (e.tierMinCharge[0] !== 1) add('essence.tierMinCharge', 'phần tử đầu phải = 1');
  if (e.tierMinCharge[e.tierMinCharge.length - 1]! > e.maxCharge)
    add('essence.tierMinCharge', 'phần tử cuối phải ≤ essence.maxCharge');
  if (e.radiusByTier.length !== e.tierMinCharge.length)
    add('essence.radiusByTier', 'phải cùng độ dài với essence.tierMinCharge');
  if (e.startCharge > e.maxCharge) add('essence.startCharge', 'phải ≤ essence.maxCharge');

  // combat
  for (const [k, list] of Object.entries(c.combat.element.beats)) {
    if (list && hasDuplicates(list)) add(`combat.element.beats.${k}`, 'danh sách không được trùng');
  }

  // debuff
  if (hasDuplicates(c.debuff.eligibleKinds)) add('debuff.eligibleKinds', 'không được trùng');

  // economy
  if (hasDuplicates(c.economy.currencies)) add('economy.currencies', 'không được trùng');

  // pvp
  const r = c.pvp.rating;
  const m = c.pvp.matchmaking;
  if (m.windowMax < m.windowStart) add('pvp.matchmaking.windowMax', 'phải ≥ windowStart');
  if (!(
    r.mmr.kFactor.placement >= r.mmr.kFactor.base && r.mmr.kFactor.base >= r.mmr.kFactor.highRating
  ))
    add('pvp.rating.mmr.kFactor', 'cần kFactor.placement ≥ kFactor.base ≥ kFactor.highRating');
  if (m.botFallbackAfterSec < m.windowStepSec)
    add('pvp.matchmaking.botFallbackAfterSec', 'phải ≥ windowStepSec');
  if (r.display.minDelta > r.display.maxDelta)
    add('pvp.rating.display.minDelta', 'phải ≤ display.maxDelta');
  const tiers = r.display.tiers;
  if (!strictlyIncreasing(tiers.map((t) => t.minDisplay)))
    add('pvp.rating.display.tiers', 'minDisplay phải tăng dần, không trùng');
  if (tiers[0]!.minDisplay !== 0)
    add('pvp.rating.display.tiers', 'tier đầu phải có minDisplay = 0');
  if (hasDuplicates(tiers.map((t) => t.id)))
    add('pvp.rating.display.tiers', 'id tier không được trùng');
  const diff = m.botFallbackDifficultyByMmr;
  if (!strictlyIncreasing(diff.map((d) => d.minMmr)))
    add('pvp.matchmaking.botFallbackDifficultyByMmr', 'minMmr phải tăng dần, không trùng');
  if (diff[0]!.minMmr !== 0)
    add('pvp.matchmaking.botFallbackDifficultyByMmr', 'phần tử đầu phải có minMmr = 0');
  if (r.display.seasonSoftResetFactor < 0 || r.display.seasonSoftResetFactor > 1)
    add('pvp.rating.display.seasonSoftResetFactor', 'phải trong [0, 1]');
  if (m.dodgePenaltySec.some((v, i) => i > 0 && v < m.dodgePenaltySec[i - 1]!))
    add('pvp.matchmaking.dodgePenaltySec', 'phải không giảm');
  if (hasDuplicates(m.regions)) add('pvp.matchmaking.regions', 'không được trùng');

  return issues;
}

/** Schema đầy đủ: object + ràng buộc chéo. */
export const configSchema = configObjectSchema.superRefine((c, ctx) => {
  for (const issue of checkConfigInvariants(c)) {
    ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
  }
});
